import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaKey, FaPlus } from 'react-icons/fa';
import {
  Page, PageHeader, Card, SearchInput, Switch, Modal, Field, CheckGrid, EmptyRow, Alert, Loading, initials,
} from '../../ui';
import { api } from '../../ui/api';

const PERMISSIONS = [
  'Sub Admins', 'Service Category', 'Manage Services', 'Audits',
  'Bookings', 'Manage Vendors', 'Users', 'Manage Staff',
];

const EMPTY = { name: '', email: '', phone: '', password: '', permissions: [] };

const Subadmin = () => {
  const [admins, setAdmins] = useState(null);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState(null); // 'add' | 'edit' | 'password'
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setAdmins(await api('/admin/all_admins'));
    } catch (e) {
      setLoadError(e.message);
      setAdmins([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (nextMode, admin = null) => {
    setCurrent(admin);
    setError('');
    setForm(admin
      ? { name: admin.name, email: admin.email, phone: admin.phone_number, password: '', permissions: admin.permissions || [] }
      : EMPTY);
    setMode(nextMode);
  };
  const close = () => setMode(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (mode === 'add') {
        await api('/admin/add_admin', {
          method: 'POST',
          body: {
            name: form.name, email: form.email, phone_number: form.phone,
            password: form.password, status: true, permissions: form.permissions,
          },
        });
      } else if (mode === 'edit') {
        await api(`/admin/edit_admin/${current.id}`, {
          method: 'PUT',
          body: { name: form.name, email: form.email, phone: form.phone, permissions: form.permissions },
        });
      } else {
        await api('/admin/update_password', {
          method: 'PUT',
          body: { id: current.id, new_password: form.password },
        });
      }
      close();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (admin) => {
    const verb = admin.status ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${verb} ${admin.name}?`)) return;
    try {
      await api(`/admin/change_status/${admin.id}`, { method: 'PUT', body: { status: !admin.status } });
      load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const q = query.toLowerCase();
  const rows = (admins || []).filter((a) =>
    a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q) || (a.phone_number || '').includes(query));

  const titles = { add: 'Add sub admin', edit: 'Edit sub admin', password: 'Reset password' };

  return (
    <Page>
      <PageHeader
        title="Sub admins"
        subtitle="People who can sign in to this panel, and what each of them can manage."
        actions={<button className="btn btn-primary" onClick={() => open('add')}><FaPlus /> Add sub admin</button>}
      />
      <Alert>{loadError}</Alert>

      <Card flush>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by name, email or phone" />
          {admins && <span className="count">{rows.length} of {admins.length}</span>}
        </div>
        {!admins ? <Loading /> : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Access</th>
                  <th>Active</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((admin) => (
                  <tr key={admin.id}>
                    <td data-label="Name">
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        <div className="avatar">{initials(admin.name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <span className="strong">{admin.name}</span>
                          <span className="sub">{admin.email}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Phone" className="nowrap">{admin.phone_number}</td>
                    <td data-label="Access">
                      <span className="muted">
                        {(admin.permissions || []).length === PERMISSIONS.length
                          ? 'Everything'
                          : `${(admin.permissions || []).length} of ${PERMISSIONS.length} areas`}
                      </span>
                    </td>
                    <td data-label="Active">
                      <Switch on={admin.status} onClick={() => toggleStatus(admin)} label="Active" />
                    </td>
                    <td className="actions-cell">
                      <button className="icon-btn" title="Edit" onClick={() => open('edit', admin)}><FaEdit /></button>
                      <button className="icon-btn" title="Reset password" onClick={() => open('password', admin)}><FaKey /></button>
                    </td>
                  </tr>
                )) : <EmptyRow cols={5}>{query ? 'No sub admins match your search.' : 'No sub admins yet.'}</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={!!mode}
        title={titles[mode]}
        subtitle={mode === 'password' && current ? `Set a new password for ${current.name}.` : undefined}
        onClose={close}
        onSubmit={submit}
        wide={mode !== 'password'}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        )}
      >
        <div className="form">
          <Alert>{error}</Alert>
          {mode !== 'password' ? (
            <>
              <div className="form-grid">
                <Field label="Full name">
                  <input className="input" value={form.name} onChange={set('name')} required />
                </Field>
                <Field label="Phone">
                  <input className="input" inputMode="tel" value={form.phone} onChange={set('phone')} required />
                </Field>
                <Field label="Email" full={mode === 'edit'}>
                  <input className="input" type="email" value={form.email} onChange={set('email')} required />
                </Field>
                {mode === 'add' && (
                  <Field label="Password">
                    <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={6} />
                  </Field>
                )}
              </div>
              <Field label="Can manage">
                <CheckGrid options={PERMISSIONS} value={form.permissions} onChange={(permissions) => setForm({ ...form, permissions })} />
              </Field>
            </>
          ) : (
            <Field label="New password" help="At least 6 characters.">
              <input className="input" type="password" value={form.password} onChange={set('password')} required minLength={6} autoFocus />
            </Field>
          )}
        </div>
      </Modal>
    </Page>
  );
};

export default Subadmin;
